import type { ServiceName } from "../../service/Service";
import type { IngressSummary } from "../ingresses";

export interface SiteRecordOverlay {
    id: string;
    hostnames?: string[] | null;
    isProd?: boolean;
}

/** Normalize GET /api/sites JSON (id→site map or array) into a flat list. */
export function sitesFromListResponse(data: unknown): SiteRecordOverlay[] {
    if (Array.isArray(data)) {
        return data.filter((site): site is SiteRecordOverlay => Boolean(site && typeof site === "object" && (site as SiteRecordOverlay).id));
    }
    if (data && typeof data === "object") {
        return Object.values(data as Record<string, SiteRecordOverlay | null | undefined>).filter(
            (site): site is SiteRecordOverlay => Boolean(site && site.id),
        );
    }
    return [];
}

export function collapseWebsiteRows(rows: IngressSummary[] | null | undefined): IngressSummary[] {
    const grouped = new Map<string, IngressSummary>();
    for (const row of rows || []) {
        if (!row?.id) {
            continue;
        }
        const key = `${row.id}::${row.service || ""}`;
        const existing = grouped.get(key);
        if (!existing) {
            grouped.set(key, {
                id: row.id,
                created: row.created,
                service: row.service,
                hostnames: [...(row.hostnames || [])],
                isProd: row.isProd,
            });
            continue;
        }
        for (const host of row.hostnames || []) {
            if (host && !existing.hostnames.includes(host)) {
                existing.hostnames.push(host);
            }
        }
        if (row.created && (!existing.created || row.created < existing.created)) {
            existing.created = row.created;
        }
        if (row.isProd) {
            existing.isProd = true;
        }
    }
    return Array.from(grouped.values());
}

export function applySiteRecordsToWebsites(
    rows: IngressSummary[] | null | undefined,
    sites: Array<SiteRecordOverlay | null | undefined> | null | undefined,
): IngressSummary[] {
    const collapsed = collapseWebsiteRows(rows);
    const byId = new Map<string, SiteRecordOverlay>();
    for (const site of sites || []) {
        if (site?.id) {
            byId.set(site.id, site);
        }
    }
    return collapsed.map((row) => {
        const site = byId.get(row.id);
        if (!site) {
            return row;
        }
        const hostnames = site.hostnames && site.hostnames.length > 0
            ? [...site.hostnames]
            : row.hostnames;
        return {
            ...row,
            hostnames,
            isProd: Boolean(site.isProd),
            service: (row.service || "blue") as ServiceName,
        };
    });
}
